import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorMode,
  SimpleGrid,
  Icon,
  Flex,
  List,
  ListItem,
  ListIcon,
  Divider,
  Badge,
  Button,
  Link,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { NavbarWithCallToAction } from '../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor } from '../../brandConfig';
import { FaCheckCircle, FaArrowRight, FaCode, FaCogs, FaRocket, FaShieldAlt, FaLayerGroup } from 'react-icons/fa';
import { GiCardRandom, GiPokerHand } from 'react-icons/gi';
import { MdDeveloperBoard } from 'react-icons/md';
import ListBuildingModal from '../../components/ListBuildingModal';

export const SmartContracts = () => {
  const { colorMode } = useColorMode();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const blockchainLogic = [
    {
      icon: FaCogs,
      title: 'Built-In Logic',
      description: 'Game logic is built directly into the blockchain itself, not as separate smart contracts.',
      color: '#3b82f6'
    },
    {
      icon: FaRocket,
      title: 'Protocol Upgrades',
      description: 'New features and functions are added through blockchain version releases and protocol upgrades.',
      color: '#64ffda'
    },
    {
      icon: FaLayerGroup,
      title: 'Primitive Functions',
      description: 'Developers access a library of primitive card game functions built into the protocol.',
      color: '#3b82f6'
    },
    {
      icon: FaCode,
      title: 'Configuration-Based',
      description: 'Build games through configuration rather than writing custom smart contract code.',
      color: '#64ffda'
    }
  ];

  const primitiveExamples = [
    'shuffle() - Cryptographically verifiable shuffling',
    'deal() - Distribute cards with commit-reveal schemes',
    'bet() - Process betting actions and update pot',
    'fold() - Handle player folding and seat management',
    'reveal() - Reveal cards at showdown',
    'evaluateHand() - Determine winning hands',
    'distributePot() - Award winnings to winners'
  ];

  const benefits = [
    'No smart contract coding required',
    'Reduced attack surface - fewer custom contracts',
    'Consistent behavior across all games',
    'Audited once, used by everyone',
    'Automatic upgrades with protocol improvements',
    'Lower gas costs from optimized native functions'
  ];

  return (
    <>
      <NavbarWithCallToAction />
      <Box minH="100vh" bg={getColor('background.main', colorMode)} py={20}>
        <Container maxW="900px">
          <VStack spacing={8} align="start">
            {/* Header */}
            <VStack spacing={4} align="start" w="full">
              <Badge
                px={4}
                py={2}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                LEARN
              </Badge>
              <Heading
                as="h1"
                size="2xl"
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Smart Contracts on Block52
              </Heading>
              <Text fontSize="xl" color={getColor('text.secondary', colorMode)} lineHeight="tall">
                Block52 takes a different approach than traditional smart contract platforms. Instead of requiring
                developers to write custom contracts, all game logic is built directly into the blockchain protocol
                itself as primitive functions.
              </Text>
            </VStack>

            <Divider />

            {/* Cosmos Architecture */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                The Cosmos Approach
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Unlike Ethereum-style blockchains where developers deploy custom smart contracts, Block52 uses the
                Cosmos SDK architecture. In Cosmos, core functionality is built directly into the blockchain's
                modules—the actual blockchain code itself.
              </Text>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                This means that instead of writing Solidity smart contracts, Block52 releases improved versions
                of the blockchain software with new features and functions over time. Developers then access these
                built-in functions through simple configuration.
              </Text>
              <Box
                p={6}
                bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
                borderRadius="lg"
                borderLeft="4px solid"
                borderColor="#3b82f6"
              >
                <Flex align="center" mb={2}>
                  <Icon as={MdDeveloperBoard} color="#3b82f6" boxSize={6} mr={3} />
                  <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Built-In Security
                  </Heading>
                </Flex>
                <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                  By building game logic directly into the blockchain protocol, Block52 reduces the attack surface
                  compared to user-deployed smart contracts. The core functions are audited once and used by everyone,
                  ensuring consistency and security across all poker games.
                </Text>
              </Box>
            </Box>

            {/* How It Works Grid */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={6} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                How It Works
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {blockchainLogic.map((item, index) => (
                  <Box
                    key={index}
                    p={6}
                    borderRadius="lg"
                    bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                    border="1px solid"
                    borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                    transition="all 0.3s"
                    _hover={{
                      transform: 'translateY(-4px)',
                      borderColor: item.color,
                      boxShadow: `0 8px 16px ${item.color}30`,
                    }}
                  >
                    <Flex align="start" mb={3}>
                      <Flex
                        w="48px"
                        h="48px"
                        borderRadius="lg"
                        bg={`${item.color}20`}
                        align="center"
                        justify="center"
                        mr={4}
                        flexShrink={0}
                      >
                        <Icon as={item.icon} color={item.color} boxSize={6} />
                      </Flex>
                      <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        {item.title}
                      </Heading>
                    </Flex>
                    <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                      {item.description}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* Primitive Functions */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Primitive Card Functions
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Block52 provides a comprehensive library of "primitive" functions—building blocks for any poker
                game. Developers combine these primitives with configuration to build their games without writing
                low-level cryptographic code.
              </Text>
              <Box
                p={6}
                bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                borderRadius="lg"
                border="1px solid"
                borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
              >
                <Flex align="center" mb={4}>
                  <Icon as={GiCardRandom} color="#3b82f6" boxSize={8} mr={3} />
                  <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Available Primitive Functions
                  </Heading>
                </Flex>
                <List spacing={2}>
                  {primitiveExamples.map((func, index) => (
                    <ListItem key={index}>
                      <Flex align="center">
                        <ListIcon as={FaCheckCircle} color="#64ffda" fontSize="lg" />
                        <Text color={getColor('text.secondary', colorMode)} fontSize="sm" fontFamily="mono">
                          {func}
                        </Text>
                      </Flex>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>

            {/* Building a Game */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Building Your Game
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                To build a poker game on Block52, developers come together with a configuration that specifies:
              </Text>
              <VStack spacing={4} align="start">
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(59, 130, 246, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#3b82f6">1</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Game Rules
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        Specify the poker variant (Texas Hold'em, Omaha, etc.), number of players, and game structure.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(100, 255, 218, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#64ffda">2</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Betting Structure
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        Define limits (No Limit, Pot Limit, Fixed Limit), blinds, antes, and minimum/maximum bets.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(59, 130, 246, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#3b82f6">3</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Rake & Economics
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        Set rake percentages, distribution to validators, and prize pool configurations for tournaments.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(100, 255, 218, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#64ffda">4</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Deploy & Play
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        Submit your configuration to the blockchain. The protocol handles all the cryptographic operations
                        automatically using the built-in primitive functions.
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              </VStack>
            </Box>

            {/* Verifiable Fairness */}
            <Box
              w="full"
              p={6}
              bgGradient={colorMode === 'dark'
                ? 'linear(to-r, rgba(59, 130, 246, 0.1), rgba(100, 255, 218, 0.1))'
                : 'linear(to-r, blue.50, teal.50)'}
              borderRadius="xl"
              border="2px solid"
              borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#3b82f6'}
            >
              <Flex align="center" mb={4}>
                <Icon as={FaShieldAlt} color="#3b82f6" boxSize={8} mr={3} />
                <Heading as="h2" size="lg" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  Verifiable Shuffling & Dealing
                </Heading>
              </Flex>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                The cool thing about this architecture is that shuffling happens through true randomness—verifiable
                random functions (VRFs) that cannot be manipulated. You can look at the randomness distribution
                and verify for yourself that games are fair.
              </Text>
              <Button
                as={Link}
                href="https://app.block52.xyz/explorer/distribution"
                isExternal
                variant="outline"
                borderColor="#3b82f6"
                color="#3b82f6"
                _hover={{
                  bg: `rgba(59, 130, 246, 0.1)`,
                  textDecoration: 'none',
                }}
                rightIcon={<ExternalLinkIcon />}
              >
                View Randomness Distribution
              </Button>
            </Box>

            {/* Benefits */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Benefits of This Approach
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {benefits.map((benefit, index) => (
                  <Flex key={index} align="center">
                    <Icon as={FaCheckCircle} color="#64ffda" fontSize="xl" mr={3} flexShrink={0} />
                    <Text color={getColor('text.secondary', colorMode)}>{benefit}</Text>
                  </Flex>
                ))}
              </SimpleGrid>
            </Box>

            {/* Future Vision */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Evolving with the Network
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                As Block52 evolves, new primitive functions and features are added through protocol upgrades.
                This means your poker club automatically benefits from improvements without needing to redeploy
                or modify your game configuration.
              </Text>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                The vision is to create a comprehensive library of card game primitives that enable anyone to
                build fair, transparent poker experiences without becoming a cryptography expert.
              </Text>
            </Box>

            {/* CTA */}
            <Box w="full" textAlign="center" py={6}>
              <Button
                size="lg"
                px={8}
                py={6}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                _hover={{
                  bgGradient: 'linear(to-r, #64ffda, #3b82f6)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.4)',
                }}
                rightIcon={<FaArrowRight />}
                borderRadius="full"
                fontWeight="bold"
                onClick={() => setIsModalOpen(true)}
              >
                Start Now
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
      <ListBuildingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default SmartContracts;
